import { useEffect, useState, useContext } from "react";
import ManagerNav from "./managernav";
import { AuthContext } from "../AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import CompanyInformation from "./companyedit";
import { differenceInCalendarDays, format } from "date-fns";
import { useNavigate } from "react-router-dom";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function parseInvoiceUrlJson(invoiceValue) {
  if (!invoiceValue) return [];

  try {
    const parsed = typeof invoiceValue === "string" ? JSON.parse(invoiceValue) : invoiceValue;
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === "object") return [parsed];
    return [];
  } catch {
    return [];
  }
}

function parseAiTransactionsJson(transactionsValue) {
  if (!transactionsValue) return [];

  try {
    const parsed = typeof transactionsValue === "string" ? JSON.parse(transactionsValue) : transactionsValue;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const TABS = [
  { name: "Billing", key: "billing" },
  { name: "Invoices", key: "invoices" },
  { name: "AI Tokens", key: "aitokens" },
  { name: "Billing Support", key: "billingsupport" },
];

const SUPPORT_OPTIONS = [
  { label: "Upgrade Plan", value: "upgrade" },
  { label: "Cancellation", value: "cancellation" },
];

const AI_TOKEN_OPTIONS = [10, 20, 30, 40, 50, 100];

export default function Billing() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("billing");
  const [billing, setBilling] = useState([]);
  const [aiTokens, setAiTokens] = useState([]);
  const [aiTokensStats, setAiTokensStats] = useState({ totalCost: 0, totalRequests: 0 });
  const [billingTickets, setBillingTickets] = useState([]);

  // Billing Support state
  const [supportOption, setSupportOption] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [supportStatus, setSupportStatus] = useState(null);
  const [newUserCount, setNewUserCount] = useState(""); // For Upgrade Plan
  const [price, setPrice] = useState(0); // For Upgrade Plan
  const [showSuccessAnim, setShowSuccessAnim] = useState(false);

  // AI Tokens Increase state
  const [aiTokenAmount, setAiTokenAmount] = useState("");
  const [aiTokenBuyStatus, setAiTokenBuyStatus] = useState(null);
  const [aiTokenBuyAnim, setAiTokenBuyAnim] = useState(false);
  const [aiTokenPrice, setAiTokenPrice] = useState(0);

  // Payment dispute state
  const [showDisputeBox, setShowDisputeBox] = useState(false);
  const [selectedDisputeInvoice, setSelectedDisputeInvoice] = useState("");
  const [disputeIssue, setDisputeIssue] = useState("");
  const [disputeStatus, setDisputeStatus] = useState(null);
  const [isSubmittingDispute, setIsSubmittingDispute] = useState(false);

  // Upgrade Success Page state
  const [showUpgradeSuccessPage, setShowUpgradeSuccessPage] = useState(false);
  const [upgradeSuccessData, setUpgradeSuccessData] = useState({});

  const user = useContext(AuthContext);
  const isTrialUser = user?.trial === true || user?.trial === 1 || user?.trial === "true" || user?.trial === "1";
  const trialEndDate = user?.trialEnd ? new Date(user.trialEnd) : null;
  const hasValidTrialEndDate = trialEndDate && !Number.isNaN(trialEndDate.getTime());
  const trialEndLabel = hasValidTrialEndDate ? format(trialEndDate, "dd/MM/yyyy") : "N/A";
  const trialDaysRemaining = hasValidTrialEndDate ? differenceInCalendarDays(trialEndDate, new Date()) : null;
  const trialDaysText =
    trialDaysRemaining === null
      ? "Trial end date unavailable"
      : trialDaysRemaining >= 0
        ? `${trialDaysRemaining} day${trialDaysRemaining === 1 ? "" : "s"} remaining`
        : `${Math.abs(trialDaysRemaining)} day${Math.abs(trialDaysRemaining) === 1 ? "" : "s"} overdue`;

  const currentBilling = billing[0] || {};
  const currentAiCredits = Number(currentBilling?.AICredits || 0);
  const nextBillingDateLabel = currentBilling?.CurrentPeriodEnd
    ? new Date(currentBilling.CurrentPeriodEnd).toLocaleDateString()
    : "your next billing cycle";
  const formatPounds = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "0.00";
    return numeric.toFixed(2);
  };

  if (!user || user.access !== "admin") {
    return (
      <div className="mx-auto mt-10 max-w-3xl rounded-2xl border border-white/35 bg-white/80 p-8 text-center text-slate-700 shadow-[0_16px_40px_rgba(15,23,42,0.16)] backdrop-blur-xl">
        You do not have permission to view this page.
      </div>
    );
  }

  const fetchData = async () => {
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/billing`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();

      setBilling(data.data || []);
      console.log("Fetched billing data:", data.data);
      setAiTokens(data.aitokens || []);
      setBillingTickets(data.billingTickets || []);

      // Calculate AI Token stats
      const totalCost = (data.aitokens || []).reduce((sum, t) => sum + (t.cost || 0), 0);
      setAiTokensStats({
        totalCost: totalCost.toFixed(2),
        totalRequests: (data.aitokens || []).length,
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const billingStats = [
    { name: "AI Credits", stat: currentAiCredits },
    { name: "AI Token Usage", stat: `$${aiTokensStats.totalCost}/$${formatPounds(currentAiCredits)}` },
    { name: "AI Requests", stat: aiTokensStats.totalRequests },
  ];

  const disputeInvoiceOptions = billing.flatMap((company) => {
    const companyName = company?.CompanyName || "Unknown Company";

    const subscriptionInvoices = parseInvoiceUrlJson(company?.InvoiceURL)
      .filter((entry) => entry?.invoiceUrl)
      .map((entry) => ({
        value: `subscription|${companyName}|${entry.invoiceUrl}`,
        source: "subscription",
        companyName,
        invoiceUrl: entry.invoiceUrl,
        date: entry?.date || null,
        label: `${companyName} - Subscription - ${entry?.date ? new Date(entry.date).toLocaleString() : "N/A"}`,
      }));

    const aiTokenInvoices = parseAiTransactionsJson(company?.AITransactions)
      .filter((entry) => entry?.invoiceUrl)
      .map((entry) => ({
        value: `ai_tokens|${companyName}|${entry.invoiceUrl}`,
        source: "ai_tokens",
        companyName,
        invoiceUrl: entry.invoiceUrl,
        date: entry?.date || null,
        label: `${companyName} - AI Tokens - ${entry?.date ? new Date(entry.date).toLocaleString() : "N/A"}`,
      }));

    return [...subscriptionInvoices, ...aiTokenInvoices];
  });

  const handleSubmitDispute = async (e) => {
    e.preventDefault();
    setDisputeStatus(null);

    const selectedOption = disputeInvoiceOptions.find((option) => option.value === selectedDisputeInvoice);
    if (!selectedOption || !disputeIssue.trim()) {
      setDisputeStatus("error");
      return;
    }

    try {
      setIsSubmittingDispute(true);
      const response = await fetch("https://api.insightos.co.uk/api/paymentdispute", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          source: selectedOption.source,
          companyName: selectedOption.companyName,
          invoiceUrl: selectedOption.invoiceUrl,
          invoiceDate: selectedOption.date,
          issue: disputeIssue.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit payment dispute");
      }

      setDisputeStatus("success");
      setSelectedDisputeInvoice("");
      setDisputeIssue("");
      navigate("/support");
    } catch (_err) {
      setDisputeStatus("error");
    } finally {
      setIsSubmittingDispute(false);
    }
  };

  // Billing Support submit handler (for non-upgrade options)
  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    setSupportStatus(null);
    try {
      const res = await fetch("https://api.insightos.co.uk/api/billingsupport", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          type: supportOption,
          message: supportMessage,
        }),
      });
      if (!res.ok) throw new Error("Failed to submit support request");
      setSupportStatus("success");
      setSupportOption("");
      setSupportMessage("");
      setNewUserCount("");
      setShowSuccessAnim(true);
      setTimeout(() => {
        setShowSuccessAnim(false);
        fetchData(); // Refresh billing data after animation
      }, 1500);
    } catch (err) {
      setSupportStatus("error");
    }
  };

  // Upgrade Plan Buy handler
  const handleUpgradeBuy = async () => {
    setSupportStatus(null);
    try {
      const res = await fetch("https://api.insightos.co.uk/api/billing/upgrade", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          newUserCount: Number(newUserCount),
          price: price,
        }),
      });
      if (!res.ok) throw new Error("Failed to upgrade plan");
      setSupportStatus("success");
      setSupportOption("");
      setSupportMessage("");
      setShowSuccessAnim(true);
      setUpgradeSuccessData({
        newUserCount,
        price,
      });
      setTimeout(() => {
        setShowSuccessAnim(false);
        setShowUpgradeSuccessPage(true);
        fetchData();
      }, 1000);
    } catch (err) {
      setSupportStatus("error");
    }
  };

  // AI Tokens Buy handler
  const handleAiTokenBuy = async () => {
    setAiTokenBuyStatus(null);
    try {
      const res = await fetch("https://api.insightos.co.uk/api/billing/aitokens", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          amount: Number(aiTokenAmount),
          successUrl: `${window.location.origin}/aitokenpayment/${Number(aiTokenAmount)}`,
          cancelUrl: `${window.location.origin}/billing`,
        }),
      });
      if (!res.ok) throw new Error("Failed to buy AI tokens");

      const data = await res.json();
      if (!data?.url) {
        throw new Error("Missing Stripe checkout URL");
      }

      window.location.assign(data.url);
      setAiTokenBuyStatus("success");
      setAiTokenAmount("");
      setAiTokenBuyAnim(true);
      setTimeout(() => {
        setAiTokenBuyAnim(false);
        fetchData();
      }, 1500);
    } catch (err) {
      setAiTokenBuyStatus("error");
    }
  };

  const newUserPrice = 50; // Updated price per new user for upgrade plan
  useEffect(() => {
    if (supportOption === "upgrade" && newUserCount) {
      const totalCost = newUserCount * newUserPrice;
      setPrice(totalCost);
    } else {
      setPrice(0);
    }
    // eslint-disable-next-line
  }, [supportOption, newUserCount]);

  // AI Token price calculation (example: $1 per token)
  useEffect(() => {
    if (aiTokenAmount) {
      setAiTokenPrice(Number(aiTokenAmount) * 1);
    } else {
      setAiTokenPrice(0);
    }
  }, [aiTokenAmount]);

  // Success page for upgrade
  if (showUpgradeSuccessPage) {
    return (
      <>
        <ManagerNav />
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-100 px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto flex min-h-[70vh] max-w-4xl flex-col items-center justify-center rounded-3xl border border-white/35 bg-white/75 p-8 shadow-[0_16px_40px_rgba(15,23,42,0.16)] backdrop-blur-xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="mb-8"
          >
            <div className="flex items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 shadow-[0_16px_32px_rgba(16,185,129,0.35)]" style={{ width: 120, height: 120 }}>
              <svg
                className="w-24 h-24 text-white"
                fill="none"
                stroke="currentColor"
                strokeWidth={4}
                viewBox="0 0 48 48"
              >
                <circle cx="24" cy="24" r="20" stroke="white" strokeWidth="4" fill="none" />
                <path
                  stroke="white"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="4"
                  d="M16 26l7 7 11-13"
                />
              </svg>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center"
          >
            <h1 className="mb-4 text-3xl font-bold text-emerald-600">Plan Updated Successfully!</h1>
            <div className="mb-2 text-xl text-slate-700">
              <span className="font-semibold">New Users:</span>{" "}
              <span className="text-indigo-600 font-bold">{upgradeSuccessData.newUserCount}</span>
            </div>
            <div className="mb-8 text-xl text-slate-700">
              <span className="font-semibold">New Price:</span>{" "}
              <span className="text-emerald-600 font-bold">£{formatPounds(upgradeSuccessData.price)}</span>
            </div>
            <button
              className="inline-flex items-center rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-6 py-3 text-lg font-medium text-white shadow-[0_12px_30px_rgba(49,46,129,0.3)] transition hover:from-indigo-800/95 hover:to-indigo-600/95 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2"
              onClick={() => {
                setShowUpgradeSuccessPage(false);
                setNewUserCount("");
                setPrice(0);
                setSupportOption("");
              }}
            >
              Back to Billing
            </button>
          </motion.div>
        </div>
        </div>
      </>
    );
  }

  if (isTrialUser) {
    return (
      <>
        <ManagerNav />
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-100 px-4 py-8 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl space-y-6">
            <div className="rounded-xl bg-red-500 px-4 py-3 text-white shadow-md">
              <p className="text-sm font-semibold leading-5">
                Trial version limited to 5 users • Ends {trialEndLabel} • {trialDaysText}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/paymenttrial', { state: { companyCode: user?.companyCode } })}
                  className="rounded-md bg-white px-2.5 py-1 text-[11px] font-semibold text-red-600"
                >
                  Upgrade Now
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/sales-training')}
                  className="rounded-md border border-white/80 px-2.5 py-1 text-[11px] font-semibold text-white"
                >
                  Speak To Sales
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-white/35 bg-white/80 p-6 text-slate-700 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl">
              Billing options are not available during trial. You can still update your company information below.
            </div>

            <CompanyInformation />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ManagerNav />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-100 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 rounded-3xl border border-white/35 bg-gradient-to-r from-indigo-950 via-sky-700 to-emerald-500 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.28)]">
          <h1 className="text-2xl font-semibold text-white">Billing Console</h1>
          <p className="mt-1 text-sm text-sky-100/90">Manage subscriptions, AI tokens, and billing support in one place.</p>
        </div>
        {/* Tabs */}
        <div className="mb-6 rounded-2xl border border-white/35 bg-white/75 p-2 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl">
          <nav className="flex gap-2" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={classNames(
                  activeTab === tab.key
                    ? "bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border-white/20 text-white shadow-lg shadow-indigo-950/40"
                    : "bg-white/55 border-indigo-200/70 text-indigo-900 hover:bg-white/75",
                  "whitespace-nowrap rounded-xl border px-4 py-2.5 font-medium text-sm transition-all"
                )}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Billing Tab */}
        {activeTab === "billing" && (
          <>
            <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2 mb-8">
              {billingStats.map((item) => (
                <div
                  key={item.name}
                  className="overflow-hidden rounded-2xl border border-white/35 bg-white/80 px-4 py-5 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:p-6"
                >
                  <dt className="truncate text-sm font-medium text-slate-500">{item.name}</dt>
                  <dd className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
                    {item.stat}
                  </dd>
                </div>
              ))}
            </dl>
            <div className="rounded-3xl border border-white/35 bg-white/80 shadow-[0_16px_40px_rgba(15,23,42,0.12)] backdrop-blur-xl">
              <div className="flex items-center justify-between border-b border-indigo-100 px-6 py-4">
                <h2 className="text-lg font-semibold text-slate-900">Billing</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-indigo-50/70">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Industry</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Payment</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Payment</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AI Transactions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-indigo-100/70 bg-transparent">
                    {billing.map((company) => {
                      const aiTransactionEntries = parseAiTransactionsJson(company.AITransactions);

                      return (
                      <tr key={company.StripeCustomerId}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{company.CompanyName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{company.ContactName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{company.Industry}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={classNames(
                            company.Status === "Active" ? "text-emerald-600" : "text-gray-500"
                          )}>
                            {company.Status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{company.Plan}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{company.Users}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">£{company.LastPaymentAmount}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {company.StartDate ? new Date(company.StartDate).toLocaleDateString() : "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {company.CurrentPeriodEnd ? new Date(company.CurrentPeriodEnd).toLocaleDateString() : "N/A"}
                        </td>
                        <td className="min-w-[280px] px-6 py-4 align-top text-sm text-slate-600">
                          {aiTransactionEntries.length > 0 ? (
                            <div className="space-y-1">
                              {aiTransactionEntries.map((entry, idx) => (
                                <div key={`${company.StripeCustomerId}-aitransaction-${idx}`} className="rounded-xl border border-emerald-200/80 bg-emerald-50/70 px-2 py-1.5">
                                  <div className="text-xs text-slate-700">
                                    <span className="font-semibold">Date:</span> {entry?.date ? new Date(entry.date).toLocaleString() : "N/A"}
                                  </div>
                                  <div className="text-xs text-slate-700">
                                    <span className="font-semibold">Amount:</span> {entry?.amount ?? "N/A"}
                                  </div>
                                  <div className="text-xs text-slate-700">
                                    <span className="font-semibold">Staff:</span> {entry?.staffname || "N/A"}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="italic text-slate-400">No AI transactions recorded</span>
                          )}
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            </div>

            <CompanyInformation />
          </>
        )}

        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <div className="space-y-6">
            <div className="mt-2 rounded-3xl border border-white/35 bg-white/80 shadow-[0_16px_40px_rgba(15,23,42,0.12)] backdrop-blur-xl">
              <div className="flex items-center justify-between border-b border-indigo-100 px-6 py-4">
                <h2 className="text-lg font-semibold text-slate-900">Invoices</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-indigo-50/70">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Company Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Invoice</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date &amp; Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-indigo-100/70 bg-transparent">
                    {billing.flatMap((company) => {
                      const invoiceEntries = parseInvoiceUrlJson(company.InvoiceURL);

                      if (invoiceEntries.length === 0) {
                        return [
                          <tr key={`${company.StripeCustomerId}-invoice-empty`}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{company.CompanyName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm italic text-slate-400">No invoice</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">N/A</td>
                          </tr>,
                        ];
                      }

                      return invoiceEntries.map((entry, idx) => (
                        <tr key={`${company.StripeCustomerId}-invoice-${idx}`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{company.CompanyName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {entry?.invoiceUrl ? (
                              <a
                                href={entry.invoiceUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="font-semibold text-indigo-700 underline hover:text-indigo-900"
                              >
                                View Invoice
                              </a>
                            ) : (
                              <span className="italic text-slate-400">No link</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            {entry?.date ? new Date(entry.date).toLocaleString() : "N/A"}
                          </td>
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="overflow-x-auto">
              <div className="rounded-3xl border border-white/35 bg-white/80 shadow-[0_16px_40px_rgba(15,23,42,0.12)] backdrop-blur-xl">
                <div className="flex items-center justify-between border-b border-indigo-100 px-6 py-4">
                  <h2 className="text-lg font-semibold text-slate-900">AI Transactions</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-indigo-50/70">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Company Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date &amp; Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Staff</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Invoice</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-indigo-100/70 bg-transparent">
                      {billing.flatMap((company) => {
                        const aiTransactionEntries = parseAiTransactionsJson(company.AITransactions);

                        if (aiTransactionEntries.length === 0) {
                          return [
                            <tr key={`${company.StripeCustomerId}-aitransaction-empty`}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{company.CompanyName}</td>
                              <td colSpan={4} className="px-6 py-4 text-sm italic text-slate-400">
                                No AI transactions recorded
                              </td>
                            </tr>,
                          ];
                        }

                        return aiTransactionEntries.map((entry, idx) => (
                          <tr key={`${company.StripeCustomerId}-aitransaction-${idx}`}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{company.CompanyName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                              {entry?.date ? new Date(entry.date).toLocaleString() : "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{entry?.amount ?? "N/A"}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{entry?.staffname || "N/A"}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {entry?.invoiceUrl ? (
                                <a
                                  href={entry.invoiceUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="font-semibold text-indigo-700 underline hover:text-indigo-900"
                                >
                                  View Invoice
                                </a>
                              ) : (
                                <span className="italic text-slate-400">No link</span>
                              )}
                            </td>
                          </tr>
                        ));
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI Tokens Tab */}
        {activeTab === "aitokens" && (
          <div className="rounded-3xl border border-white/35 bg-white/80 shadow-[0_16px_40px_rgba(15,23,42,0.12)] backdrop-blur-xl">
            <div className="flex items-center justify-between border-b border-indigo-100 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">AI Token Usage</h2>
              <span className="text-sm text-slate-500">
                Total Cost: <span className="font-bold text-indigo-600">${aiTokensStats.totalCost}</span>
              </span>
            </div>
            <div className="flex flex-col items-center gap-4 border-b border-indigo-100 px-6 py-4 md:flex-row">
              <label className="mr-2 block text-sm font-medium text-slate-700">
                Buy AI Tokens:
              </label>
              <span className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
                Available Credits: {currentAiCredits}
              </span>
              <select
                className="block w-40 rounded-xl border border-white/35 bg-white/85 px-3 py-2 text-sm text-slate-700 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                value={aiTokenAmount}
                onChange={e => {
                  setAiTokenAmount(e.target.value);
                  setAiTokenBuyStatus(null);
                }}
              >
                <option value="">Select amount</option>
                {AI_TOKEN_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              {aiTokenAmount && (
                <span className="text-sm text-slate-600">
                  Price: <span className="font-bold text-emerald-500">${aiTokenPrice}.00 </span>
                </span>
              )}
              <button
                type="button"
                className="inline-flex items-center rounded-xl border border-white/20 bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 px-4 py-2 text-sm font-medium text-white shadow-[0_12px_30px_rgba(5,150,105,0.28)] transition hover:from-emerald-500/95 hover:to-emerald-400/95 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2"
                disabled={aiTokenBuyAnim || !aiTokenAmount}
                onClick={handleAiTokenBuy}
                data-component="billing-ai-tokens"
                data-feature="buy_tokens_post"
              >
                Buy
              </button>
              <AnimatePresence>
                {aiTokenBuyAnim && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="flex justify-center items-center"
                  >
                    <motion.div
                      className="rounded-full bg-emerald-500 flex items-center justify-center"
                      style={{ width: 48, height: 48 }}
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    >
                      <svg
                        className="w-8 h-8 text-white"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={3}
                        viewBox="0 0 24 24"
                      >
                        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" fill="none" />
                        <path
                          stroke="white"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                          d="M8 12l3 3 5-5"
                        />
                      </svg>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
              {aiTokenBuyStatus === "error" && (
                <div className="text-red-600 text-sm ml-2">Failed to buy. Please try again.</div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-indigo-50/70">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tokens</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Staff Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Staff ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-indigo-100/70 bg-transparent">
                  {aiTokens.map((token) => (
                    <tr key={token.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{token.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{token.model}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{token.tokens}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{token.companyCode}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {token.createdAt ? new Date(token.createdAt).toLocaleString() : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{token.staffName || "N/A"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{token.staffId || "N/A"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                        ${token.cost ? token.cost : "0.00"}
                      </td>
                    </tr>
                  ))}
                  {aiTokens.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-6 py-4 text-center text-gray-400">
                        No AI token usage found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Billing Support Tab */}
        {activeTab === "billingsupport" && user.access === "admin" && (
          <div className="mx-auto max-w-3xl rounded-3xl border border-white/35 bg-white/80 p-8 shadow-[0_16px_40px_rgba(15,23,42,0.12)] backdrop-blur-xl">
            <div className="flex flex-col items-center text-center">
              <img
                src="/stripe-logo.jpg"
                alt="Stripe"
                className="mb-5 h-10 w-auto"
              />
              <h2 className="text-2xl font-semibold text-slate-900">Manage Billing In Stripe</h2>
              <p className="mt-2 max-w-xl text-sm text-slate-600">
                Open your Stripe billing portal to update payment details and manage your subscription.
              </p>

              <a
                href="https://billing.stripe.com/p/login/14A28r6u98Ig2wAgdf4sE00"
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex items-center rounded-xl border border-white/20 bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(37,99,235,0.25)] transition hover:from-indigo-500 hover:to-blue-500"
              >
                Open Stripe Billing
              </a>

              <p className="mt-4 break-all rounded-xl border border-indigo-100 bg-indigo-50/70 px-3 py-2 text-xs text-indigo-700">
                https://billing.stripe.com/p/login/14A28r6u98Ig2wAgdf4sE00
              </p>

              <button
                type="button"
                onClick={() => {
                  setShowDisputeBox((prev) => !prev);
                  setDisputeStatus(null);
                }}
                className="mt-4 inline-flex items-center rounded-xl border border-red-200 bg-red-50 px-5 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-100"
              >
                Dispute Payment
              </button>

              {showDisputeBox && (
                <form
                  onSubmit={handleSubmitDispute}
                  className="mt-5 w-full max-w-2xl rounded-2xl border border-red-100 bg-white p-5 text-left shadow-sm"
                >
                  <h3 className="text-base font-semibold text-slate-900">Submit Payment Dispute</h3>
                  <p className="mt-1 text-xs text-slate-600">
                    Select an invoice from subscription invoices or AI token invoices and describe the issue.
                  </p>

                  <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Select Invoice
                  </label>
                  <select
                    value={selectedDisputeInvoice}
                    onChange={(e) => {
                      setSelectedDisputeInvoice(e.target.value);
                      setDisputeStatus(null);
                    }}
                    className="mt-1 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    required
                  >
                    <option value="">Choose an invoice</option>
                    {disputeInvoiceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Issue
                  </label>
                  <textarea
                    value={disputeIssue}
                    onChange={(e) => {
                      setDisputeIssue(e.target.value);
                      setDisputeStatus(null);
                    }}
                    rows={4}
                    required
                    placeholder="Describe the payment issue"
                    className="mt-1 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />

                  {disputeStatus === "success" && (
                    <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                      Payment dispute submitted successfully.
                    </p>
                  )}
                  {disputeStatus === "error" && (
                    <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      Unable to submit dispute. Please check required fields and try again.
                    </p>
                  )}

                  <div className="mt-4 flex justify-end">
                    <button
                      type="submit"
                      disabled={isSubmittingDispute || disputeInvoiceOptions.length === 0}
                      className="inline-flex items-center rounded-xl border border-white/20 bg-gradient-to-r from-red-600 to-rose-600 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(220,38,38,0.25)] transition hover:from-red-500 hover:to-rose-500 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isSubmittingDispute ? "Submitting..." : "Submit Dispute"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
      </div>
    </>
  );
}
